import clsx from 'clsx';
import { type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, forwardRef, useId } from 'react';

export const estiloEntrada =
  'h-9 w-full rounded-campo border bg-superficie px-3 text-tinta placeholder:text-tinta-2/70 transition-colors duration-150 hover:border-tinta-2/50 disabled:bg-fundo disabled:text-tinta-2';

interface Moldura {
  rotulo: string;
  erro?: string;
  ajuda?: ReactNode;
  className?: string;
}

function MolduraCampo({ id, rotulo, erro, ajuda, className, children }: Moldura & { id: string; children: ReactNode }) {
  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-[13px] leading-[18px] font-medium text-tinta">
        {rotulo}
      </label>
      {children}
      {erro ? (
        <p id={`${id}-erro`} className="text-[13px] leading-[18px] text-erro">
          {erro}
        </p>
      ) : ajuda ? (
        <p id={`${id}-ajuda`} className="text-[13px] leading-[18px] text-tinta-2">
          {ajuda}
        </p>
      ) : null}
    </div>
  );
}

type PropsCampo = Moldura & Omit<InputHTMLAttributes<HTMLInputElement>, 'className'>;

export const Campo = forwardRef<HTMLInputElement, PropsCampo>(function Campo(
  { rotulo, erro, ajuda, className, id: idExterno, ...entrada },
  ref,
) {
  const idGerado = useId();
  const id = idExterno ?? idGerado;
  return (
    <MolduraCampo id={id} rotulo={rotulo} erro={erro} ajuda={ajuda} className={className}>
      <input
        ref={ref}
        id={id}
        aria-invalid={erro ? true : undefined}
        aria-describedby={erro ? `${id}-erro` : ajuda ? `${id}-ajuda` : undefined}
        className={clsx(estiloEntrada, erro ? 'border-erro' : 'border-linha')}
        {...entrada}
      />
    </MolduraCampo>
  );
});

type PropsSelecao = Moldura & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'>;

export const Selecao = forwardRef<HTMLSelectElement, PropsSelecao>(function Selecao(
  { rotulo, erro, ajuda, className, id: idExterno, children, ...entrada },
  ref,
) {
  const idGerado = useId();
  const id = idExterno ?? idGerado;
  return (
    <MolduraCampo id={id} rotulo={rotulo} erro={erro} ajuda={ajuda} className={className}>
      <select
        ref={ref}
        id={id}
        aria-invalid={erro ? true : undefined}
        className={clsx(estiloEntrada, 'cursor-pointer pr-8', erro ? 'border-erro' : 'border-linha')}
        {...entrada}
      >
        {children}
      </select>
    </MolduraCampo>
  );
});

type PropsCaixa = { rotulo: ReactNode; descricao?: string } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

export const CaixaMarcar = forwardRef<HTMLInputElement, PropsCaixa>(function CaixaMarcar(
  { rotulo, descricao, id: idExterno, className, ...entrada },
  ref,
) {
  const idGerado = useId();
  const id = idExterno ?? idGerado;
  return (
    <div className={clsx('flex items-start gap-2.5', className)}>
      <input ref={ref} id={id} type="checkbox" className="mt-0.5 size-4 cursor-pointer accent-acao" {...entrada} />
      <label htmlFor={id} className="cursor-pointer">
        <span className="font-medium">{rotulo}</span>
        {descricao && <span className="block text-[13px] leading-[18px] text-tinta-2">{descricao}</span>}
      </label>
    </div>
  );
});
